/**
 * Axios interceptor + token storage for Safari ITP cross-site cookie fallback.
 *
 * The backend sets an httpOnly SameSite=None; Secure cookie on login/register/
 * google-session. That works on Chrome/Firefox. Safari, however, often blocks
 * third-party cookies (frontend on panel.notalia.app, backend on *.emergentagent.com),
 * so /auth/me returns 401 even though the cookie was "Set-Cookie"'d.
 *
 * To work around it we also return the token in the response body, persist it
 * in localStorage and attach it as `Authorization: Bearer ...` on every request.
 * The backend already accepts the Bearer header as a fallback to the cookie.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import axios from "axios";

const TOKEN_KEY = "notalia_session_token";
const SUPERADMIN_TOKEN_KEY = "notalia_superadmin_token";

export function setAuthToken(token) {
  if (!token) return;
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* localStorage unavailable (private mode, quota) — cookie remains primary */
  }
}

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearAuthToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Save the current token as the superadmin backup, then activate newToken.
 * Used when starting impersonation so we can restore later.
 */
export function saveAndReplaceToken(newToken) {
  if (!newToken) return;
  const current = getAuthToken();
  try {
    if (current) localStorage.setItem(SUPERADMIN_TOKEN_KEY, current);
    localStorage.setItem(TOKEN_KEY, newToken);
  } catch {
    /* noop */
  }
}

/**
 * Restore the previously saved superadmin token and clear the impersonation token.
 * Returns the restored token, or null if there was nothing saved.
 */
export function restoreSuperadminToken() {
  try {
    const saved = localStorage.getItem(SUPERADMIN_TOKEN_KEY);
    if (saved) {
      localStorage.setItem(TOKEN_KEY, saved);
      localStorage.removeItem(SUPERADMIN_TOKEN_KEY);
      return saved;
    }
  } catch {
    /* noop */
  }
  return null;
}

/**
 * Install global axios interceptors. Call once at app bootstrap.
 * - withCredentials: true so the cookie flows on browsers that allow it
 * - Authorization: Bearer <token> so Safari works even if the cookie was blocked
 */
export function installAuthInterceptors() {
  axios.defaults.withCredentials = true;

  axios.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });
}
